/* Flow Music Lab — product gallery + lightbox navigation.

   1. Overlay prev/next arrows on the media gallery: advance by
      clicking the neighbouring thumbnail, which routes through
      Dawn's media-gallery logic (active media, slider scroll,
      aria-current, live-region announcement). Thumbnails may be
      visually hidden (mobile) — they still exist and stay in sync.

   2. Prev/next + counter + keyboard navigation inside the product
      media modal (lightbox), which Dawn ships without navigation.

   Event delegation keeps everything working if product-info
   re-renders. */

(() => {
  // The gallery snippet can be injected again (quick-add fetches the
  // product page; theme editor re-renders sections) — never register
  // the document-level listeners twice.
  if (window.fmlProductGalleryLoaded) return;
  window.fmlProductGalleryLoaded = true;

  // --- Main gallery overlay arrows --------------------------------

  // Current slide index. On mobile the viewer is a scroll slider and
  // thumbnail aria-current lags behind swipes (media-gallery.js updates
  // it via a 500ms-debounced slideChanged handler), so prefer the
  // slider-component's own state, which updates synchronously.
  const currentIndex = (gallery, thumbnails) => {
    const viewer = gallery.querySelector('slider-component[id^="GalleryViewer"]');
    const slide =
      viewer && viewer.sliderItemsToShow && viewer.currentPage
        ? viewer.sliderItemsToShow[viewer.currentPage - 1]
        : null;
    if (slide) {
      const index = thumbnails.findIndex(
        (thumbnail) => thumbnail.closest('[data-target]')?.dataset.target === slide.dataset.mediaId
      );
      if (index >= 0) return index;
    }
    return thumbnails.findIndex(
      (thumbnail) => thumbnail.getAttribute('aria-current') === 'true'
    );
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fml-gallery-prev], [data-fml-gallery-next]');
    if (!button) return;

    const gallery = button.closest('media-gallery');
    if (!gallery) return;

    const thumbnails = Array.from(gallery.querySelectorAll('.thumbnail-list__item .thumbnail'));
    if (thumbnails.length < 2) return;

    const direction = button.hasAttribute('data-fml-gallery-next') ? 1 : -1;
    const current = currentIndex(gallery, thumbnails);
    const target = ((current < 0 ? 0 : current) + direction + thumbnails.length) % thumbnails.length;
    thumbnails[target].click();
  });

  // --- Lightbox (product media modal) navigation -------------------

  const modalItems = (modal) =>
    Array.from(modal.querySelectorAll('.product-media-modal__content > [data-media-id]')).filter(
      (element) => !element.classList.contains('product__media-item--variant')
    );

  const syncCounter = (modal) => {
    const items = modalItems(modal);
    const index = items.findIndex((element) => element.classList.contains('active'));
    const current = modal.querySelector('[data-fml-modal-current]');
    const total = modal.querySelector('[data-fml-modal-total]');
    if (current) current.textContent = String((index < 0 ? 0 : index) + 1);
    if (total) total.textContent = String(items.length);
  };

  const setModalActive = (modal, index) => {
    const items = modalItems(modal);
    if (!items.length) return;
    const target = ((index % items.length) + items.length) % items.length;

    items.forEach((element, i) => element.classList.toggle('active', i === target));
    syncCounter(modal);

    // Keep the gallery behind the modal on the same media, so closing
    // the lightbox doesn't jump back to another image — and retarget
    // openedBy so Dawn's hide() returns focus to the matching slide.
    const gallery = document.getElementById(modal.id.replace('ProductModal', 'MediaGallery'));
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      const sectionId = modal.id.replace('ProductModal-', '');
      const mediaId = items[target].dataset.mediaId;
      gallery.setActiveMedia(`${sectionId}-${mediaId}`, false);
      const opener = gallery.querySelector(`.product__media-toggle[data-media-id="${mediaId}"]`);
      if (opener && modal.openedBy) modal.openedBy = opener;
    }
  };

  const stepModal = (modal, direction) => {
    const items = modalItems(modal);
    const current = items.findIndex((element) => element.classList.contains('active'));
    setModalActive(modal, (current < 0 ? 0 : current) + direction);
  };

  // Dawn's ModalDialog closes the media modal on any mouse pointerup
  // inside it (global.js `media-modal` handler) — stop arrow/counter
  // presses from reaching it. Capture phase, so it runs first.
  document.addEventListener(
    'pointerup',
    (event) => {
      if (
        event.target.closest(
          '[data-fml-modal-prev], [data-fml-modal-next], .fml-modal-nav__counter'
        )
      ) {
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fml-modal-prev], [data-fml-modal-next]');
    if (!button) return;
    const modal = button.closest('product-modal');
    if (!modal) return;
    stepModal(modal, button.hasAttribute('data-fml-modal-next') ? 1 : -1);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const modal = document.querySelector('product-modal[open]');
    if (!modal) return;
    event.preventDefault();
    stepModal(modal, event.key === 'ArrowRight' ? 1 : -1);
  });

  // Initialise the counter whenever a lightbox opens.
  const observeModals = () => {
    document.querySelectorAll('product-modal').forEach((modal) => {
      new MutationObserver(() => {
        if (modal.hasAttribute('open')) syncCounter(modal);
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeModals);
  } else {
    observeModals();
  }
})();
